import React, { useState, useEffect, useRef } from 'react';
import { Leaf, TreeDeciduous, Users, Globe } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const ImpactCounter = () => {
  const { language } = useLanguage();
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  // Impact stats (these could come from an API)
  const stats = [
    {
      icon: TreeDeciduous,
      value: 12847,
      label: 'Trees Planted',
      label_es: 'Árboles Plantados',
      color: 'text-green-500',
      bgColor: 'bg-green-500/10',
    },
    {
      icon: Leaf,
      value: 458,
      suffix: 'tons',
      suffix_es: 'ton',
      label: 'Carbon Offset',
      label_es: 'Carbono Compensado',
      color: 'text-emerald-500',
      bgColor: 'bg-emerald-500/10',
    },
    {
      icon: Users,
      value: 5200,
      suffix: '+',
      label: 'Happy Travelers',
      label_es: 'Viajeros Felices',
      color: 'text-[#84bc00]',
      bgColor: 'bg-[#84bc00]/10',
    },
    {
      icon: Globe,
      value: 100,
      suffix: '%',
      label: 'Eco-Certified',
      label_es: 'Eco-Certificado',
      color: 'text-teal-500',
      bgColor: 'bg-teal-500/10',
    },
  ];

  // Intersection Observer to trigger animation when visible
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // Animated counter hook
  const useCounter = (end, duration = 2000) => {
    const [count, setCount] = useState(0);
    
    useEffect(() => {
      if (!isVisible) return;
      
      let startTime = null;
      const animate = (timestamp) => {
        if (!startTime) startTime = timestamp;
        const progress = Math.min((timestamp - startTime) / duration, 1);
        setCount(Math.floor(progress * end));
        if (progress < 1) {
          requestAnimationFrame(animate);
        }
      };
      requestAnimationFrame(animate);
    }, [end, duration, isVisible]);

    return count;
  };

  return (
    <section 
      ref={sectionRef}
      className="py-16 px-4 bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900"
      data-testid="impact-counter-section"
    >
      <div className="max-w-6xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-12">
          <span className="inline-block px-4 py-2 bg-[#84bc00]/20 text-[#84bc00] text-sm font-bold rounded-full mb-4">
            {language === 'es' ? 'NUESTRO IMPACTO' : 'OUR IMPACT'}
          </span>
          <h2 className="text-2xl sm:text-3xl font-black text-white">
            {language === 'es' 
              ? 'Tu lista de deseos no debería dañar el planeta' 
              : "Your bucket list shouldn't break the planet"}
          </h2>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6">
          {stats.map((stat, index) => {
            const IconComponent = stat.icon;
            const AnimatedValue = () => {
              const count = useCounter(stat.value);
              return (
                <span className={`text-3xl sm:text-4xl lg:text-5xl font-black ${stat.color}`}>
                  {count.toLocaleString()}{stat.suffix || ''}
                </span>
              );
            };

            return (
              <div
                key={index}
                data-testid={`impact-stat-${index}`}
                className={`
                  ${stat.bgColor} rounded-2xl p-4 sm:p-6 text-center
                  transform transition-all duration-500
                  ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'}
                `}
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                <div className={`
                  w-12 h-12 rounded-full ${stat.bgColor} ${stat.color}
                  flex items-center justify-center mx-auto mb-4
                `}>
                  <IconComponent className="w-6 h-6" />
                </div>
                <AnimatedValue />
                <p className="text-gray-400 text-sm mt-2 font-medium">
                  {language === 'es' ? stat.label_es : stat.label}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default ImpactCounter;
